import { useState, type FormEvent, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '../ui';
import { fileApiService } from '../../services/api/fileApiService';
import { ApiError } from '../../services/api/apiClient';
import type { BecasProfile } from '../../services/api/becasApiService';

const inputClass =
  'w-full rounded-xl bg-brand-darkNavy border border-brand-lightBlue/40 px-4 py-3 text-white placeholder:text-dark-500 focus:outline-none focus:border-brand-yellow transition-colors';

const POSITIONS = ['GK', 'CB', 'LB', 'RB', 'CDM', 'CM', 'CAM', 'LM', 'RM', 'LW', 'RW', 'CF', 'ST'] as const;
const ENGLISH_LEVELS = ['A1', 'A2', 'B1', 'B2', 'C1', 'C2', 'native'] as const;
const ACADEMIC_LEVELS = ['high_school', 'technical', 'university', 'graduate'] as const;
const NATIONALITIES = ['CL', 'AR', 'CO', 'PE', 'MX', 'US', 'ES', 'BR', 'UY', 'EC', 'other'] as const;

function years(): number[] {
  const y = new Date().getFullYear();
  const out: number[] = [];
  for (let i = y - 2; i <= y + 8; i += 1) out.push(i);
  return out;
}

interface Props {
  initial?: Partial<BecasProfile> | null;
  defaults?: { first_name?: string; last_name?: string };
  onSaved: (profile: BecasProfile) => Promise<void>;
}

export function BecasProfileForm({ initial, defaults, onSaved }: Props) {
  const { t } = useTranslation();
  const [form, setForm] = useState<BecasProfile>({
    first_name: initial?.first_name || defaults?.first_name || '',
    last_name: initial?.last_name || defaults?.last_name || '',
    birth_date: initial?.birth_date?.slice(0, 10) || '',
    nationality: initial?.nationality || '',
    position: initial?.position || '',
    photo_url: initial?.photo_url || '',
    academic_level: initial?.academic_level || '',
    graduation_year: initial?.graduation_year || '',
    gpa: initial?.gpa ?? '',
    english_level: initial?.english_level || '',
    reel_url: initial?.reel_url || '',
    motivation: initial?.motivation || '',
    guardian_name: initial?.guardian_name || '',
    guardian_phone: initial?.guardian_phone || '',
    guardian_email: initial?.guardian_email || '',
  });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const set = (key: keyof BecasProfile, value: string | number) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const handlePhoto = async (file: File | undefined) => {
    if (!file) return;
    setUploading(true);
    setError(null);
    try {
      const uploaded = await fileApiService.upload(file);
      set('photo_url', uploaded.url);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('becas.errors.upload'));
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!form.photo_url) {
      setError(t('becas.profile.photoRequired'));
      return;
    }
    if (!form.guardian_name || !form.guardian_phone || !form.guardian_email) {
      setError(t('becas.profile.guardianRequired'));
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const payload: BecasProfile = {
        ...form,
        graduation_year: Number(form.graduation_year),
        gpa: form.gpa === '' || form.gpa === undefined ? undefined : Number(form.gpa),
      };
      await onSaved(payload);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('becas.errors.generic'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid sm:grid-cols-2 gap-4">
        <Field label={t('becas.profile.firstName')}>
          <input className={inputClass} required minLength={2} value={form.first_name} onChange={(e) => set('first_name', e.target.value)} />
        </Field>
        <Field label={t('becas.profile.lastName')}>
          <input className={inputClass} required minLength={2} value={form.last_name} onChange={(e) => set('last_name', e.target.value)} />
        </Field>
        <Field label={t('becas.profile.birthDate')}>
          <input type="date" className={inputClass} required value={form.birth_date} onChange={(e) => set('birth_date', e.target.value)} />
        </Field>
        <Field label={t('becas.profile.nationality')}>
          <select className={inputClass} required value={form.nationality} onChange={(e) => set('nationality', e.target.value)}>
            <option value="">{t('becas.profile.select')}</option>
            {NATIONALITIES.map((code) => (
              <option key={code} value={code}>
                {t(`becas.nationalities.${code}`)}
              </option>
            ))}
          </select>
        </Field>
        <Field label={t('becas.profile.position')}>
          <select className={inputClass} required value={form.position} onChange={(e) => set('position', e.target.value)}>
            <option value="">{t('becas.profile.select')}</option>
            {POSITIONS.map((pos) => (
              <option key={pos} value={pos}>
                {t(`becas.positions.${pos}`)} ({pos})
              </option>
            ))}
          </select>
        </Field>
        <Field label={t('becas.profile.academicLevel')}>
          <select className={inputClass} required value={form.academic_level} onChange={(e) => set('academic_level', e.target.value)}>
            <option value="">{t('becas.profile.select')}</option>
            {ACADEMIC_LEVELS.map((level) => (
              <option key={level} value={level}>
                {t(`becas.academic.${level}`)}
              </option>
            ))}
          </select>
        </Field>
        <Field label={t('becas.profile.graduationYear')}>
          <select className={inputClass} required value={String(form.graduation_year)} onChange={(e) => set('graduation_year', e.target.value)}>
            <option value="">{t('becas.profile.select')}</option>
            {years().map((y) => (
              <option key={y} value={y}>
                {y}
              </option>
            ))}
          </select>
        </Field>
        <Field label={t('becas.profile.gpa')}>
          <input type="number" step="0.01" min="0" max="10" required className={inputClass} value={form.gpa ?? ''} onChange={(e) => set('gpa', e.target.value)} />
        </Field>
        <Field label={t('becas.profile.englishLevel')}>
          <select className={inputClass} required value={form.english_level} onChange={(e) => set('english_level', e.target.value)}>
            <option value="">{t('becas.profile.select')}</option>
            {ENGLISH_LEVELS.map((level) => (
              <option key={level} value={level}>
                {t(`becas.english.${level}`)}
              </option>
            ))}
          </select>
        </Field>
        <Field label={t('becas.profile.reelUrl')}>
          <input type="url" required className={inputClass} placeholder="https://" value={form.reel_url || ''} onChange={(e) => set('reel_url', e.target.value)} />
        </Field>
      </div>

      <Field label={t('becas.profile.photo')}>
        <input
          type="file"
          accept="image/*"
          onChange={(e) => void handlePhoto(e.target.files?.[0])}
          className="block w-full text-sm text-dark-300 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:bg-brand-yellow file:text-brand-darkNavy file:font-semibold"
        />
        {uploading && <p className="text-xs text-brand-yellow mt-2">{t('becas.documents.uploading')}</p>}
        {form.photo_url && (
          <img src={form.photo_url} alt="" className="mt-3 h-20 w-20 rounded-xl object-cover border border-brand-lightBlue/40" />
        )}
      </Field>

      <Field label={t('becas.profile.motivation')}>
        <textarea
          className={`${inputClass} min-h-[120px]`}
          required
          minLength={20}
          value={form.motivation}
          onChange={(e) => set('motivation', e.target.value)}
        />
      </Field>

      <div className="rounded-2xl border border-brand-yellow/30 bg-brand-yellow/5 p-4 space-y-3">
        <p className="text-sm font-semibold text-brand-yellow">{t('becas.profile.guardianTitle')}</p>
        <Field label={t('becas.profile.guardianName')}>
          <input className={inputClass} required value={form.guardian_name || ''} onChange={(e) => set('guardian_name', e.target.value)} />
        </Field>
        <Field label={t('becas.profile.guardianPhone')}>
          <input className={inputClass} required value={form.guardian_phone || ''} onChange={(e) => set('guardian_phone', e.target.value)} />
        </Field>
        <Field label={t('becas.profile.guardianEmail')}>
          <input type="email" className={inputClass} required value={form.guardian_email || ''} onChange={(e) => set('guardian_email', e.target.value)} />
        </Field>
      </div>

      {error && (
        <p className="text-sm text-accent-rose bg-accent-rose/10 border border-accent-rose/30 rounded-xl px-4 py-3">
          {error}
        </p>
      )}

      <Button type="submit" variant="primary" fullWidth isLoading={saving || uploading}>
        {t('becas.profile.continue')}
      </Button>
    </form>
  );
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <label className="block">
      <span className="block text-sm font-semibold text-white mb-2">{label}</span>
      {children}
    </label>
  );
}
